// "Confirm appointment" task for pipeline (seller/buyer) appointments.
// One task per pipeline entry: created when an appointment is set, moved when it changes,
// removed when it is cleared. Nothing is sent: the task's Text button opens the agent's own texting app.
import { ref, push, set, update, remove } from 'firebase/database';
import { database } from '../firebaseConfig';

export const CONFIRM_SOURCE = 'pipeline-appt-confirm';
const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Due the day before at 10 AM; if that has already passed, due today.
export const confirmDue = (apptAt, now = new Date()) => {
  const a = new Date(apptAt);
  const d = new Date(a.getFullYear(), a.getMonth(), a.getDate() - 1, 10, 0, 0);
  const pick = d < now ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), Math.min(now.getHours() + 1, 23), 0, 0) : d;
  return { dueDate: isoDay(pick), dueTime: `${pad(pick.getHours())}:00`, dueAt: pick.toISOString() };
};

export const confirmMessage = ({ firstName, agentName, apptAt, address }) => {
  const a = new Date(apptAt);
  const day = a.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const time = a.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `Hi ${firstName || 'there'}, this is ${agentName || 'your agent'} with RESIDENCE | eXp Realty. Just confirming our appointment ${day} at ${time}${address ? ` at ${address}` : ''}. Does that still work for you?`;
};

export const smsHref = (phone, body) => {
  const d = String(phone || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `sms:+1${d}?&body=${encodeURIComponent(body || '')}` : '';
};

// Decide what to do. Returns { action: 'none'|'create'|'update'|'remove'|'move', ... }.
export const planConfirm = ({ apptAt, agent, prevFor, prevTaskId, prevOwner }) => {
  if (!apptAt || !agent) return prevTaskId ? { action: 'remove', owner: prevOwner } : { action: 'none' };
  if (!prevTaskId) return { action: 'create' };
  if (prevOwner && prevOwner !== agent) return { action: 'move', owner: prevOwner };
  if (prevFor !== apptAt) return { action: 'update' };
  return { action: 'none' };
};

// Applies the plan. `entry` = { kind: 'seller'|'buyer', id, firstName, name, phone, address, apptAt, agent, agentName }.
// Returns the fields to save on the pipeline record (confirmTaskId / confirmTaskFor / confirmTaskOwner).
export const syncConfirmTask = async (entry, prev, uid) => {
  const p = planConfirm({ apptAt: entry.apptAt, agent: entry.agent, prevFor: prev.confirmTaskFor, prevTaskId: prev.confirmTaskId, prevOwner: prev.confirmTaskOwner });
  if (p.action === 'none') return {};
  if (p.action === 'remove' || p.action === 'move') {
    try { await remove(ref(database, `users/${p.owner || entry.agent}/tasks/${prev.confirmTaskId}`)); } catch (e) { console.error('Could not remove old confirm task', e); }
    if (p.action === 'remove') return { confirmTaskId: null, confirmTaskFor: null, confirmTaskOwner: null };
  }
  const who = entry.name || 'client';
  const fields = {
    name: `Confirm appointment with ${who}`, title: `Confirm appointment with ${who}`,
    description: `Appointment ${new Date(entry.apptAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${entry.address ? ` at ${entry.address}` : ''}.${entry.phone ? ` Phone: ${entry.phone}.` : ' No phone on file.'}`,
    type: 'Follow-up', source: CONFIRM_SOURCE, pipelineKind: entry.kind,
    [entry.kind === 'buyer' ? 'buyerInterestId' : 'sellerInterestId']: entry.id,
    clientName: who, clientPhone: entry.phone || '', apptAt: new Date(entry.apptAt).toISOString(),
    smsBody: confirmMessage({ firstName: entry.firstName, agentName: entry.agentName, apptAt: entry.apptAt, address: entry.address }),
    ...confirmDue(entry.apptAt), updatedAt: new Date().toISOString(),
  };
  if (p.action === 'update') {
    await update(ref(database, `users/${entry.agent}/tasks/${prev.confirmTaskId}`), { ...fields, status: 'pending', completedAt: null });
    return { confirmTaskFor: entry.apptAt };
  }
  const r = push(ref(database, `users/${entry.agent}/tasks`));
  await set(r, { role: 'Agent', appliesTo: 'All', mode: 'task', status: 'pending', assignee: entry.agent, createdAt: new Date().toISOString(), createdBy: uid || null, completedAt: null, notes: '', ...fields });
  return { confirmTaskId: r.key, confirmTaskFor: entry.apptAt, confirmTaskOwner: entry.agent };
};
